import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  IconButton,
  Portal,
  TextField,
  Typography,
} from '@material-ui/core';
import { makeStyles, darken } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import { useHistory } from 'react-router-dom';

import { useUpdateUserProfileMutation, UserStatus } from 'api';
import { useCurrentUser, useNotify } from 'hooks';
import { logout } from 'paths';
import { Loaders } from 'components';

const useStyles = makeStyles(theme => ({
  content: {
    paddingTop: 0,
  },
  deleteButton: {
    marginRight: theme.spacing(),
    color: theme.palette.error.main,
    '&:hover': {
      color: darken(theme.palette.error.main, 0.1),
      backgroundColor: 'transparent',
    },
  },
  confirmDeleteButton: {
    color: '#FFF',
    background: theme.palette.error.main,
    '&:hover': {
      background: darken(theme.palette.error.main, 0.1),
    },
  },
}));

const Delete: FC = () => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [confirming, setConfirming] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState('');
  const currentUser = useCurrentUser();
  const notify = useNotify();
  const { push } = useHistory();
  const [updateUserProfile, { loading: isDeleting }] = useUpdateUserProfileMutation({
    onCompleted: () => push(logout),
    onError: notify.mutationError,
  });

  function handleDelete() {
    updateUserProfile({
      variables: {
        data: {
          id: currentUser.id,
          user_status: UserStatus.Deleted,
        },
      },
    });
  }

  function renderConfirmDialog() {
    if (!confirming) {
      return null;
    }
    return (
      <Portal>
        <Dialog
          onClose={() => setConfirming(false)}
          open={confirming}
          data-testid="change_password_dialog">
          {renderConfirmDialogTitle()}
          {renderConfirmDialogContent()}
        </Dialog>
      </Portal>
    );
  }

  function renderConfirmDialogTitle() {
    return (
      <DialogTitle disableTypography>
        <Typography variant="subtitle2">{t('user:areYouSure')}</Typography>
        <IconButton onClick={() => setConfirming(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
    );
  }

  function renderConfirmDialogContent() {
    return (
      <DialogContent className={classes.content}>
        <Box pb={2}>
          <Typography>{t('user:deleteCannotBeReversed')}</Typography>
        </Box>
        <Box pb={2}>
          <TextField
            InputLabelProps={{ shrink: !!confirmEmail }}
            value={confirmEmail}
            onChange={event => setConfirmEmail(event.target.value)}
            label={t('user:email')}
            placeholder={t('user:email')}
            fullWidth
          />
        </Box>
        <Grid container justify="flex-end">
          <Grid item>
            <Button
              size="large"
              type="button"
              variant="contained"
              disabled={confirmEmail !== currentUser.email_address}
              className={classes.confirmDeleteButton}
              onClick={handleDelete}>
              {isDeleting ? (
                <Loaders.Standard color="white" height={26} />
              ) : (
                t('user:confirmDeleteMyAccount')
              )}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    );
  }

  function renderButton() {
    return (
      <Button
        size="large"
        type="button"
        variant="outlined"
        className={classes.deleteButton}
        onClick={() => setConfirming(true)}>
        {t('user:deleteMyAccount')}
      </Button>
    );
  }

  return (
    <Fragment>
      {renderConfirmDialog()}
      {renderButton()}
    </Fragment>
  );
};

export default Delete;
